<div class="single-playlist layout-spacing">
    <div class="single-playlist__header">
        <div class="single-playlist__header--left">
            <h3 class="single-playlist__header--title">{{ playlist()?.playlistName }}</h3>
            <p class="single-playlist__header--description">
                {{ playlist()?.description }}
            </p>
            <div class="single-playlist__licenses" data-testid="single-playlist-licenses-div">
                <span class="single-playlist__licenses--label">Assigned Licenses:</span>
                @if (isLoadingLicenses() || !playlist()) {
                    <div class="single-playlist__licenses--list">
                        @for (_ of [1, 2, 3]; track $index) {
                            <span class="single-playlist__licenses--chip-skeleton"></span>
                        }
                    </div>
                } @else if (assignedLicenses().length > 0) {
                    <div class="single-playlist__licenses--list">
                        @for (item of assignedLicenses(); track item.id) {
                            <span class="single-playlist__licenses--chip">
                                {{ item.licenseKey }}
                                <svg
                                    class="single-playlist__licenses--copy-icon"
                                    title="Click to copy"
                                    viewBox="0 0 13 14"
                                    [appCopy]="item.licenseKey">
                                    <use href="assets/icons/icons-sprite.svg#copy"></use>
                                </svg>
                            </span>
                        }
                    </div>
                } @else {
                    <span class="single-playlist__licenses--empty">No licenses assigned</span>
                }
            </div>
        </div>
        <div class="single-playlist__header--right flex items-center gap-3">
            <app-button
                label="Assign License"
                iconKey="licenses"
                size="xl"
                gap="gap-3"
                (click)="showAssignLicenseModal.set(true)" />
            <app-button
                label="Add content"
                iconKey="plus"
                size="xl"
                gap="gap-3"
                (click)="onOpenAddContentModal()" />
        </div>
    </div>

    <div class="single-playlist__toolbar">
        <p class="single-playlist__toolbar--title">
            Media Contents <span>All contents ({{ paginationMeta().totalCount }})</span>
        </p>
        @if (showFilterBy()) {
            <app-table-toolbar
                [buttons]="[]"
                buttonIconPlacement="left"
                (searchValueChange)="onSearchValueChange($event)">
                <div leftActions>
                    <app-filter-dropdown type="grouped" [groupedOptions]="contentFiltersGroup" />
                </div>

                <div rightActionsEnd class="toolbar__view-controls">
                    @for (button of viewControls; track button.action) {
                        <svg class="icon icon--hoverable" width="40" height="40">
                            <use
                                [attr.xlink:href]="
                                    'assets/icons/icons-sprite.svg#' + button.icon
                                "></use>
                        </svg>
                    }
                </div>
            </app-table-toolbar>
        }
    </div>

    @if (isLoadingContents() && !isLoadingMoreContents()) {
        <app-thumbnail-skeleton [skeletonCount]="40" skeletonSize="content-grid" />
    } @else if (contents().length > 0) {
        <div class="single-playlist__grid grid-contents">
            @for (item of contents(); track item.id) {
                <div class="single-playlist__thumb">
                    <ntv-thumbnail-item
                        [disableRightClick]="true"
                        [menuOptionVisible]="false"
                        [clickable]="true"
                        [item]="$any(item)"
                        size="lg"
                        [customWidth]="'100%'"
                        [customHeight]="'172px'"
                        [customTextSize]="'12px'"
                        [showThumbnailInfoTag]="false"
                        [showActionButtons]="false"
                        [eyeIconVisible]="false" />
                    <button
                        type="button"
                        class="single-playlist__thumb-delete"
                        title="Remove from playlist"
                        data-testid="single-playlist-thumb-delete"
                        (click)="onRequestRemoveContent(item)">
                        <svg width="11" height="11" viewBox="0 0 14 14">
                            <use href="assets/icons/icons-sprite.svg#delete"></use>
                        </svg>
                    </button>
                </div>
            }
        </div>
    } @else {
        <app-empty title="No Records Found" animationWidth="300px" animationHeight="300px" />
    }

    @if (contents().length > 0) {
        <div class="single-playlist__pagination">
            <p class="single-playlist__pagination-text">
                Showing {{ contents().length }} out of {{ paginationMeta().totalCount }}
            </p>
            @if (paginationMeta().hasNextPage) {
                <button class="single-playlist__pagination-btn" (click)="onNextPage()">
                    @if (isLoadingMoreContents()) {
                        <span>Loading...</span>
                    } @else {
                        <span>Show More</span>
                        <svg width="14" height="14">
                            <use
                                [attr.xlink:href]="'assets/icons/icons-sprite.svg#chevron-right'"></use>
                        </svg>
                    }
                </button>
            }
        </div>
    }
</div>

<!-- Add content stepper modal -->
@if (showAddContentModal()) {
    <app-add-content
        [showAddContentModal]="showAddContentModal()"
        [selectedContentType]="selectedContentType()!"
        [playlistId]="$safeNavigationMigration(playlist()?.playlistId)!"
        [dealerId]="playlist()?.ownerId ?? null"
        (contentAdded)="onContentAdded()"
        (modalClose)="showAddContentModal.set(false)" />
}

<!-- Assign License Modal -->
<app-assign-license-modal
    [isVisible]="showAssignLicenseModal()"
    [excludeLicenseIds]="assignedLicenseIdsAcrossPlaylists()"
    [ownerId]="$safeNavigationMigration(playlist()?.ownerId)"
    (modalClose)="showAssignLicenseModal.set(false)"
    (assign)="onAssignLicense($event)" />

<!-- Assign License Result Modal -->
@if (showAssignResultModal()) {
    <app-confirmation-modal
        [isVisible]="showAssignResultModal()"
        [type]="assignResultModalConfig().type"
        [title]="assignResultModalConfig().title"
        [message]="assignResultModalConfig().message"
        [loopAnimation]="false"
        confirmText="Close"
        (confirm)="onAssignResultModalClose()"
        (modalClose)="onAssignResultModalClose()" />
}

<!-- Remove Content Confirmation Modal -->
@if (showRemoveContentModal()) {
    <app-confirmation-modal
        type="delete"
        [isVisible]="showRemoveContentModal()"
        [isLoading]="isRemovingContent()"
        title="Remove Content"
        [message]="'Remove ' + (contentToRemove()?.name ?? 'this content') + ' from the playlist?'"
        confirmText="Remove"
        cancelText="Cancel"
        [showCancelButton]="true"
        (confirm)="onConfirmRemoveContent()"
        (cancel)="onCancelRemoveContent()"
        (modalClose)="onCancelRemoveContent()" />
}

<!-- Remove Content Success Modal -->
@if (showRemoveSuccessModal()) {
    <app-confirmation-modal
        type="success"
        [isVisible]="showRemoveSuccessModal()"
        [loopAnimation]="false"
        title="Content Removed"
        message="The content was successfully removed from this playlist."
        confirmText="Close"
        (confirm)="onRemoveSuccessModalClose()"
        (modalClose)="onRemoveSuccessModalClose()" />
}
